import Image from "next/image";
import BlackLogo from "@public/logo-black.png";
import AppStoreLogo from "@public/qr-app-store.png";
import PlayStoreLogo from "@public/qr-google-play.png";
import AppGalleryLogo from "@public/qr-app-gallery.png";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { Trans } from "@lingui/macro";
import { useRouter } from "next/router";
import {
  LinkProps,
  SocialMediaIcons,
  generateDomainPath,
  generateSubdomainPath,
  redirectedBrowserTab,
  sharedLinks,
} from "./shared/link-props";
import { SocialProfileJsonLd } from "next-seo";
import Link from "next/link";
import { ReactElement } from "react";

const lastTwoLinks = sharedLinks.slice(-2);

const Footer: React.FunctionComponent = () => {
  const router = useRouter();
  const { locale, pathname, asPath } = router;
  const seoLocale = "/" + locale;

  const currentYear = new Date().getFullYear();

  const VARIANT_MAPS = {
    ...{
      backgroundColor: "bg-light-background",
      textColor: "text-text-darkest",
      linkColor: "text-text-medium",
      bottomLinkColor: "text-text-lightest",
      borderColor: "border-text-light-background",
      copyRightColor: "text-text-lightest",
    },
  };

  const DISCOVER_ITEMS: LinkProps[] = sharedLinks.slice(0, 8);

  const BUSINESS_ITEMS: LinkProps[] = sharedLinks.slice(8, 13);

  const HELP_ITEMS: LinkProps[] = sharedLinks.slice(-5, -2);

  const selectedLocale =
    (locale || router.defaultLocale) === "en" ? "" : locale;

  // To open a new tab if logo or link of footer section is clicked on subdomain pages
  const showNewTab = pathname.startsWith("/_subdomain");

  // To remove last scroll position of home if home logo is clicked
  const clearHomeSession = () => {
    sessionStorage.removeItem(`scrollPos:/`);
  };

  return (
    <footer className={`${VARIANT_MAPS.backgroundColor} px-8 pt-16 pb-20`}>
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col items-center lg:items-start">
          <div onClick={clearHomeSession}>
            <Link href={generateDomainPath(`/${locale}`)}>
              <a target={showNewTab ? "_blank" : undefined}>
                <div className="">
                  <Image
                    src={BlackLogo}
                    height={24}
                    width={104}
                    alt="logo"
                    priority
                  />
                </div>
              </a>
            </Link>
          </div>
          <div className="mt-8 flex w-full flex-col items-center gap-8 lg:flex-row lg:justify-between">
            <div className="grid w-full flex-1 shrink-0 grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-4 md:gap-y-0 md:pr-8">
              <FooterSitemapColumn
                title={<Trans>Discover</Trans>}
                links={DISCOVER_ITEMS}
                asPath={asPath}
                showNewTab={showNewTab}
                locale={locale}
                selectedLocale={selectedLocale}
                linkColor={VARIANT_MAPS.linkColor}
              />
              <FooterSitemapColumn
                title={<Trans>Business</Trans>}
                links={BUSINESS_ITEMS}
                asPath={asPath}
                showNewTab={showNewTab}
                locale={locale}
                selectedLocale={selectedLocale}
                linkColor={VARIANT_MAPS.linkColor}
              />
              <FooterSitemapColumn
                title={<Trans>Help Centre</Trans>}
                links={HELP_ITEMS}
                asPath={asPath}
                showNewTab={showNewTab}
                locale={locale}
                selectedLocale={selectedLocale}
                linkColor={VARIANT_MAPS.linkColor}
              />
            </div>
            <div>
              <FooterContactButton />
            </div>
          </div>
        </div>
        <div
          className={`border-t ${VARIANT_MAPS.borderColor} mx-8 my-8 lg:mx-0`}
        ></div>
        <div className="flex flex-col lg:flex-row lg:justify-between">
          <FooterStoreLogo />
          <div className="my-6 flex items-start justify-center space-x-4 text-primary4">
            <SocialMediaIcons tiktokColor="blue" />
          </div>
        </div>

        <div className="flex flex-col justify-between space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4">
          <div className="flex flex-col text-center lg:flex-row lg:items-center lg:space-x-3 lg:text-left">
            <span className={`font-sen text-sm ${VARIANT_MAPS.copyRightColor}`}>
              © Copyright Lumi {currentYear}
            </span>
            <span
              className={`mt-4 font-sen text-sm lg:mt-0 ${VARIANT_MAPS.copyRightColor}`}
            >
              Lumi Global Sdn Bhd (1407575-K)
            </span>
          </div>
          <div className="flex flex-col items-center space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
            {lastTwoLinks.map((link) => (
              <div key={link.href}>
                {redirectedBrowserTab(
                  link,
                  asPath,
                  showNewTab,
                  locale,
                  "btmFooter",
                  VARIANT_MAPS.copyRightColor
                )}
              </div>
            ))}
            <div
              className={`flex items-center font-sen text-sm ${VARIANT_MAPS.bottomLinkColor}`}
            >
              <EnvelopeIcon className="mr-1 h-5 w-6" aria-hidden="true" />
              <a href="mailto:hello@luminews.my">hello@luminews.my</a>
            </div>
            <div
              className={`flex items-center font-sen text-sm ${VARIANT_MAPS.bottomLinkColor}`}
            >
              <PhoneIcon className="mr-1 h-5 w-6" aria-hidden="true" />
              <a href="tel:+60123395864">+60 12-339 5864</a>
            </div>
          </div>
        </div>
      </div>
      <SocialProfileJsonLd
        type="Organization"
        name="Lumi News Malaysia"
        url={`https://luminews.my${seoLocale.includes("en") ? "" : seoLocale}${
          router.asPath
        }`}
        sameAs={[
          "https://www.tiktok.com/@luminews.my",
          "https://www.instagram.com/luminews.my/",
          "https://www.facebook.com/luminews.my/",
          "https://twitter.com/luminewsmy",
          "https://discord.com/invite/3Qzh6S3gJy",
        ]}
      />
    </footer>
  );
};

const FooterSitemapColumn: React.FunctionComponent<{
  title: ReactElement | string;
  links: LinkProps[];
  asPath: string;
  showNewTab: boolean;
  locale?: string;
  selectedLocale?: string;
  linkColor: string;
}> = ({
  title,
  links,
  asPath,
  showNewTab,
  locale,
  selectedLocale,
  linkColor,
}) => {
  const renderFooterLink = (link: LinkProps) => {
    const linkClassName = `block font-sen ${linkColor} transition-colors duration-300 hover:text-text-dark`;

    if (link.external) {
      const href = link.withLocale
        ? link.href + (selectedLocale || "")
        : link.href;

      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          {link.title}
        </a>
      );
    }

    const href = link.subdomain
      ? generateSubdomainPath(link.href, locale)
      : generateDomainPath(link.href, locale);
    const openInNewTab = link.subdomain ? !showNewTab : showNewTab;

    return openInNewTab ? (
      <a href={href} target="_blank" rel="noreferrer" className={linkClassName}>
        {link.title}
      </a>
    ) : (
      <Link href={href}>
        <a className={linkClassName}>{link.title}</a>
      </Link>
    );
  };

  return (
    <>
      <div className="flex flex-col">
        <p className="mb-2 font-sen text-xl font-bold text-black">{title}</p>
        <div className="space-y-3">
          {links.map((link) => (
            <div key={link.href}>{renderFooterLink(link)}</div>
          ))}
        </div>
      </div>
    </>
  );
};

const FooterContactButton: React.FunctionComponent = () => {
  return (
    <>
      <div className="w-[285px] flex-col rounded-2xl bg-white p-6 shadow-custom lg:w-full">
        <div className="mb-4 block lg:hidden">
          <div className="flex flex-col items-center">
            <p className="text-4xl">📣 </p>
            <p className="text-center font-poppins text-2xl font-bold">
              Want your news to be heard?
            </p>
          </div>
        </div>
        <div className="mb-4 hidden lg:block">
          <p className="font-poppins text-2xl font-bold">
            📣 Want your news to be heard?
          </p>
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:gap-4">
          <a
            className="transition-color flex-1 cursor-pointer rounded-2xl bg-primary p-4 text-center font-sen text-white duration-300 hover:bg-primary2"
            href="mailto:justin@luminews.my"
          >
            <Trans>Advertise with us</Trans>
          </a>
          <Link href="/publisher-application" passHref>
            <a className="transition-color flex-1 cursor-pointer rounded-2xl bg-secondary p-4 text-center font-sen text-primary2 duration-300 hover:bg-light-background">
              <Trans>Join as a publisher</Trans>
            </a>
          </Link>
        </div>
      </div>
    </>
  );
};

const FooterStoreLogo: React.FunctionComponent<{
  forceRow?: boolean;
  customLogoClassName?: string;
}> = ({ forceRow = false, customLogoClassName }) => {
  const containerClassName = forceRow
    ? "flex flex-row items-center space-x-4"
    : "flex flex-col items-center space-y-6 lg:flex-row lg:space-y-0 lg:space-x-4";

  const defaultLogoClassName = forceRow
    ? "relative h-[32px] w-[120px] cursor-pointer overflow-hidden rounded-lg"
    : "relative h-[56px] w-[185px] cursor-pointer overflow-hidden rounded-lg lg:h-[32px] lg:w-[120px]";

  const logoClassName = customLogoClassName ?? defaultLogoClassName;

  return (
    <div className={containerClassName}>
      <div className={logoClassName}>
        <a href="https://apps.apple.com/us/app/lumi-news-malaysia/id1569349004?platform=iphone">
          <Image
            src={AppStoreLogo}
            alt="Apple App Store"
            layout="fill"
            objectFit="cover"
          />
        </a>
      </div>
      <div className={logoClassName}>
        <a href="https://play.google.com/store/apps/details?id=global.lumi.lumi">
          <Image
            src={PlayStoreLogo}
            alt="Google Play Store"
            layout="fill"
            objectFit="cover"
          />
        </a>
      </div>
      <div className={logoClassName}>
        <a href="https://appgallery.huawei.com/#/app/C106856271">
          <Image
            src={AppGalleryLogo}
            alt="Huawei App Gallery"
            layout="fill"
            objectFit="cover"
          />
        </a>
      </div>
    </div>
  );
};

export default Footer;
export { FooterStoreLogo };
